import React, { useMemo, useCallback, useEffect } from 'react';
import * as projectsEditDialogConfig from '@material-bank/projects-edit-dialog-config';
import {
  useCurrentProject,
  useProjectsProvider,
  useValidateProjectData,
} from '@material-bank/projects-utils';
import { AnalyticsEventName } from '@mb/lib';
import { useIntl } from 'react-intl';
import { useMediaQuery } from 'react-responsive';
import * as Styled from './HotspotMainWrapper.styled';
import { sendAnalytics } from '../api/events';
import { ProjectModalListener } from '../components/ProjectsModal/ProjectsModal';
import { cartItemsSelector } from '../redux/selectors/commonSelector';
import {
  setIsProjectModalOpen,
  setProjectModalEventLocation,
} from '../redux/sliceCreators/projectsSlice';
import { useAppSelector, useAppDispatch } from '../redux/store';
import { ResultCard } from './Card/ResultCard';
import {
  hotspotResultsSelector,
  xRequestIdSelector,
} from '../redux/selectors/hotspotSelector';
import { removeCartItemFromQueue } from '../redux/sliceCreators/commonSlice';
import { CartItem, ProductSearchMaterial } from '../types/material';

const activeBagTime = 2500;

export const HotspotMaterials = () => {
  const isMobile = useMediaQuery({ query: '(max-width: 767px)' });
  const hotspotResults = useAppSelector(hotspotResultsSelector);
  const { activeProjects } = useProjectsProvider();
  const { isProjectDataComplete } = useValidateProjectData();
  const cartItems = useAppSelector(cartItemsSelector);
  const xRequestId = useAppSelector(xRequestIdSelector);
  const { currentProject, setCurrentProjectId } = useCurrentProject();
  const dispatch = useAppDispatch();
  const intl = useIntl();

  // If something was added to Cart, show isAdded icon for X secconds,
  // and then remove icon
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      const oldCartItems = cartItems?.filter(
        (item: CartItem) => now - item.time >= activeBagTime,
      );
      if (oldCartItems?.length > 0) {
        dispatch(removeCartItemFromQueue(oldCartItems));
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [cartItems, dispatch]);

  const structuredProjects = useMemo(() => {
    return Object.values(activeProjects).map((project) => ({
      id: project.externalId,
      label: project.title,
    }));
  }, [activeProjects]);

  const cartItemsIds = useMemo(() => {
    return cartItems?.map((item: CartItem) => item.entityId) || [];
  }, [cartItems]);

  const currentProjectData = {
    id: currentProject?.externalId.toString(),
    label: currentProject?.title ?? 'Select project..',
  };

  const handleProjectCreate = useCallback(
    (params?: { analyticsEventLocation?: string }) => {
      const { analyticsEventLocation } = params ?? {};

      dispatch(setIsProjectModalOpen(true));

      if (analyticsEventLocation) {
        dispatch(setProjectModalEventLocation(analyticsEventLocation));
      }
    },
    [dispatch],
  );

  const handleProjectClick = (
    projectId: string,
    options?: { analyticsEventLocation?: string },
  ) => {
    const { analyticsEventLocation } = options ?? {};

    const project = activeProjects[projectId];

    if (project) {
      if (!isProjectDataComplete(project)) {
        projectsEditDialogConfig.openProjectEditDialog({
          projectId: project.externalId,
          eventLocation: analyticsEventLocation,
        });
      } else {
        setCurrentProjectId(project?.externalId?.toString());

        mitGTM.sendEvent({
          event: AnalyticsEventName.SET_PROJECT,
          event_location: analyticsEventLocation,
          action: 'Select',
        });
      }
    }
  };

  const handleProductClick = async (product: ProductSearchMaterial) => {
    sendAnalytics({ id: [String(product?.entityId)], type: 'view' });
    window.dispatchEvent(
      new CustomEvent('initMiniPDP', {
        detail: {
          id: product.entityId,
          product,
          isProduct: product.isProducts,
          isMaterial: !product.isProducts,
          clickedProductGridIndex: product.gridIndex,
          clickedProductRequestId: xRequestId,
        },
      }),
    );
  };

  return (
    <div>
      <Styled.MaterialsGridWrapper>
        {hotspotResults?.map((data: ProductSearchMaterial) => (
          <Styled.MaterialCell key={data.sku}>
            <ResultCard
              key={data?.sku}
              material={data}
              isMultiselect={false}
              isSelected={false}
              projects={structuredProjects}
              currentProject={currentProject}
              selectedProject={currentProjectData}
              cartItemsIds={cartItemsIds}
              onProductClick={handleProductClick}
              onCreateProjectClick={handleProjectCreate}
              onProjectClick={handleProjectClick}
              isMobile={isMobile}
              analyticsEventLocation="Visual Search Modal"
              intl={intl}
            />
          </Styled.MaterialCell>
        ))}
        <ProjectModalListener />
      </Styled.MaterialsGridWrapper>
    </div>
  );
};
